import jsPDF from 'jspdf';
import type { GeneratedPNR } from './pnr-generator';

export interface OnwardTicketDocument {
  pnr: GeneratedPNR;
  passengerInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    passportNumber: string;
    nationality: string;
  };
  flightDetails: {
    from: string;
    to: string;
    departDate: string;
    returnDate?: string;
  };
  plan: 'basic' | 'standard' | 'premium';
}

/**
 * Document Generator
 * Generates embassy-approved onward ticket PDFs
 */
export class DocumentGenerator {
  /**
   * Generate onward ticket PDF
   */
  static generateOnwardTicketPDF(data: OnwardTicketDocument): jsPDF {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header
    doc.setFillColor(37, 99, 235); // Blue
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('ONWARD FLIGHT TICKET', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Embassy-Approved Travel Document', pageWidth / 2, 30, { align: 'center' });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Booking Reference Section
    let yPos = 55;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('BOOKING REFERENCE', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(16);
    doc.text(data.pnr.bookingReference, 20, yPos + 8);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('PNR CODE', pageWidth - 80, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(16);
    doc.text(data.pnr.pnr, pageWidth - 80, yPos + 8);

    // Passenger Information
    yPos = 80;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos, pageWidth - 30, 8, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('PASSENGER INFORMATION', 20, yPos + 5.5);

    yPos += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Name:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data.passengerInfo.firstName} ${data.passengerInfo.lastName}`, 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Date of Birth:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.passengerInfo.dateOfBirth, 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Passport:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.passengerInfo.passportNumber, 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Nationality:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.passengerInfo.nationality, 60, yPos);

    // Flight Details
    yPos += 15;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos, pageWidth - 30, 8, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FLIGHT DETAILS', 20, yPos + 5.5);

    yPos += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Airline:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data.pnr.airline} (${data.pnr.airlineCode})`, 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('From:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.flightDetails.from, 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('To:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(data.flightDetails.to, 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Departure:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date(data.flightDetails.departDate).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }), 60, yPos);

    if (data.flightDetails.returnDate) {
      yPos += 7;
      doc.setFont('helvetica', 'bold');
      doc.text('Return:', 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(new Date(data.flightDetails.returnDate).toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      }), 60, yPos);
    }

    // Validity Information
    yPos += 15;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, yPos, pageWidth - 30, 8, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('VALIDITY INFORMATION', 20, yPos + 5.5);

    yPos += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Valid Until:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date(data.pnr.validUntil).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }), 60, yPos);

    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Plan Type:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    const planName = data.plan === 'basic' ? 'Basic (48 hours)' : 
                     data.plan === 'standard' ? 'Standard (7 days)' : 
                     'Premium (14 days)';
    doc.text(planName, 60, yPos);

    // Important Notes
    yPos += 15;
    doc.setFillColor(255, 243, 205);
    doc.rect(15, yPos, pageWidth - 30, 35, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('IMPORTANT NOTES:', 20, yPos + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('• This is a valid flight reservation with an active PNR in the airline booking system.', 20, yPos + 12);
    doc.text('• This document is accepted by embassies and consulates worldwide as proof of onward travel.', 20, yPos + 18);
    doc.text('• The PNR can be verified directly with the airline using the code provided above.', 20, yPos + 24);
    doc.text('• Please ensure all passenger details match your passport exactly.', 20, yPos + 30);

    // Footer
    yPos = pageHeight - 30;
    doc.setFillColor(37, 99, 235);
    doc.rect(0, yPos, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Dial A Flight', pageWidth / 2, yPos + 10, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('24/7 Support | support@dialaflight.com', pageWidth / 2, yPos + 16, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}`, pageWidth / 2, yPos + 22, { align: 'center' });

    return doc;
  }

  /**
   * Download PDF
   */
  static downloadPDF(doc: jsPDF, filename: string): void {
    doc.save(filename);
  }

  /**
   * Get PDF as blob
   */
  static getPDFBlob(doc: jsPDF): Blob {
    return doc.output('blob');
  }
}